import { Injectable, inject } from '@angular/core';
import { BadRequestDto, IcLookupTypeEnumDto } from '@data-access/bulk-operations-api';
import {
  WebSocketClient,
  WsActionV3Completed,
  WsServerPayloadEnum,
} from '@data-access/bulk-operations-ws';
import { IcEmployeesService } from '@item-cache';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { timer } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';

import { ToasterService } from '#shared/components/toaster/toaster.service';

@Injectable({ providedIn: 'root' })
@UntilDestroy()
export class EmployeeActionsToastService {
  private readonly webSocketClient = inject(WebSocketClient);
  private readonly toasterService = inject(ToasterService);
  private readonly icEmployeesMapSignal = inject(IcEmployeesService).valuesMapSignal;

  constructor() {
    this.subscribeToWebSocket();
  }

  private subscribeToWebSocket() {
    this.webSocketClient
      .getMessagesByType(WsServerPayloadEnum.EmployeeCardEditActionCompleted)
      .pipe(
        untilDestroyed(this),
        tap((data) => this.handleWebSocketEvent(data)),
      )
      .subscribe();
  }

  private handleWebSocketEvent(data: WsActionV3Completed) {
    if (!data.errorMessage) {
      this.waitForIcUpsert(data);
    } else {
      this.showErrorToast(data.employeeId, data.errorMessage);
    }
  }

  private showSuccessToast(data: WsActionV3Completed) {
    const employee = this.icEmployeesMapSignal().get(data.employeeId);

    const employeeNumber = employee?.customId;
    const fullName = [employee?.firstName, employee?.lastName].filter(Boolean).join(' ');
    const namePart = fullName ? `${fullName}` : '';

    const toastMessage = $localize`data were saved!`;

    this.toasterService.showSuccess(
      `${employeeNumber} - ${namePart} ${toastMessage}`,
      data.actionV3Id,
    );
  }

  private showErrorToast(employeeId: string, errorMessage: string) {
    const errorMsg = $localize`Employee data were not saved!`;
    const errorId = `${employeeId}:${errorMessage}`;
    this.toasterService.showError(`${errorMsg} ${errorMessage}`, errorId);
  }

  private showWarningToast(employeeId: string) {
    const employee = this.icEmployeesMapSignal().get(employeeId);

    const employeeNumber = employee?.customId;
    const fullName = [employee?.firstName, employee?.lastName].filter(Boolean).join(' ');
    const namePart = fullName ? `${fullName}` : '';

    const warningMsg = $localize`Missing update confirmation for ${employeeNumber} - ${namePart}. Please check the changes.`;
    const warningId = `${employeeId}:${warningMsg}`;
    this.toasterService.showWarning(`${warningMsg}`, warningId);
  }

  private waitForIcUpsert(data: WsActionV3Completed) {
    const employeeId = data.employeeId;
    const employeeUpsertedSub = this.webSocketClient
      .getMessagesByType(WsServerPayloadEnum.IcUpserted)
      .pipe(
        untilDestroyed(this),
        filter((data) => data.type === IcLookupTypeEnumDto.Employee),
        filter(
          (data) =>
            data.employeeItems.some((item) => item.id === employeeId) ||
            data.positionItems.some((item) => item.employeeId === employeeId),
        ),
        tap(() => this.showSuccessToast(data)),
      );

    timer(10000)
      .pipe(takeUntil(employeeUpsertedSub))
      .subscribe({
        next: () => {
          this.showWarningToast(employeeId);
        },
      });
  }

  public handleSaveError(error: BadRequestDto): void {
    const errorMsg = $localize`Error while saving changes!`;
    const errorDetails = this.formatErrorDetails(error);

    this.toasterService.showError(
      `${errorMsg} ${errorDetails}`,
      error?.generalError || 'saveError',
    );
  }

  public handleEditNoWork(employeeId: string) {
    this.showErrorToast(employeeId, $localize`No changes.`);
  }

  public handleCreateEmployeeSuccess(personalDetails: PersonalDetails) {
    this.toasterService.showSuccess(
      $localize`New position for employee ${personalDetails.employeeNumber} - ${personalDetails.firstName} ${personalDetails.lastName} was created successfully`,
      null,
    );
  }

  public handleCreateDraftSuccess(personalDetails: Partial<PersonalDetails>) {
    const { employeeNumber, firstName, lastName } = personalDetails;

    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    const namePart = fullName ? ` - ${fullName}` : '';

    const message = $localize`New draft employee ${employeeNumber}${namePart} was created successfully`;

    this.toasterService.showSuccess(message, null);
  }

  public handleEmploymentTypeChangeSuccess(personalDetails: PersonalDetails) {
    this.toasterService.showSuccess(
      $localize`Successfully changed position for ${personalDetails.employeeNumber} - ${personalDetails.firstName} ${personalDetails.lastName}`,
      null,
    );
  }

  public handleHiringSuccess(personalDetails: PersonalDetails) {
    this.toasterService.showSuccess(
      $localize`${personalDetails.employeeNumber} - ${personalDetails.firstName} ${personalDetails.lastName} was hired successfully`,
      null,
    );
  }

  public handleHiringError(error: string | BadRequestDto): void {
    const errorMsg = $localize`Unhandled error when creating employee.`;

    if (typeof error === 'string') {
      this.toasterService.showError(`${errorMsg} ${error}`, 'unhandled-error-creating-employee');
      return;
    }

    const errorDetails = this.formatErrorDetails(error);

    this.toasterService.showError(
      `${errorMsg} ${errorDetails}`,
      'unhandled-error-creating-employee',
    );
  }

  public handleDeleteDraftSuccess(personalDetails: Partial<PersonalDetails>) {
    const { employeeNumber, firstName, lastName } = personalDetails;

    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    const namePart = fullName ? ` - ${fullName}` : '';

    const message = $localize`Draft employee ${employeeNumber}${namePart} was deleted successfully`;

    this.toasterService.showSuccess(message, null);
  }

  public handleAssignUserSuccess(personalDetails: PersonalDetails): void {
    this.toasterService.showSuccess(
      $localize`User creation message successfully sent for employee ${personalDetails.employeeNumber} - ${personalDetails.firstName} ${personalDetails.lastName}`,
      null,
    );
  }

  public handleAssignUserForSaveDraftError(): void {
    const message = $localize`The user can not be created when saving a draft. Please promote the employee to create a user or remove the create user checkbox.`;
    this.toasterService.showError(message, 'assign-user-for-save-draft-error');
  }

  public handleAssignUserError(personalDetails: PersonalDetails): void {
    const message = $localize`User creation failed for employee ${personalDetails.employeeNumber} - ${personalDetails.firstName} ${personalDetails.lastName}`;
    this.toasterService.showError(message, 'assign-user-error');
  }

  private formatErrorDetails(error: BadRequestDto | undefined): string {
    return error?.validationItems
      ? error.validationItems.map((item) => `${item.propertyName}: "${item.message}"`).join('; ')
      : error?.generalError || $localize`Unknown error`;
  }
}

type PersonalDetails = {
  employeeNumber: string;
  firstName: string;
  lastName: string;
};
